import { cn } from 'utils/classNames';

export type Size = 'default' | 'large' | 'medium' | 'small' | 'xLarge';
export const SIZES: Size[] = ['small', 'default', 'medium', 'large', 'xLarge'];

interface Props {
	className?: string;

	/** If the standard top right absolute position should be used. */
	isPositioned?: boolean;

	/** If the bubble should use sale colors. */
	isSale?: boolean;

	/** A small label above the main text. */
	label?: string;

	/** Size of the bubble */
	size?: Size;

	/** The large main text. */
	text: string;
}

/** A circle intended for short text. */
export default function DiscountBubble({
	className,
	isPositioned = false,
	isSale = false,
	label,
	size = 'default',
	text,
}: Props) {
	return (
		<div
			className={cn(
				'inline-flex flex-col items-center justify-center',
				// NOTE: Don't add padding to this aspect-ratio container without
				// checking Safari, version 26.5 introduced a bug where the ratio
				// breaks if there is padding. Bug is there as of June 2026.
				'aspect-square rounded-full',
				isSale ? 'bg-[#111]' : 'bg-brandDecorative',
				'text-negative',
				'text-center whitespace-nowrap',
				'forced-colors-outline',
				isPositioned && 'absolute top-r-md right-r-md',
				size === 'small' && 'min-h-24 min-w-24',
				size === 'default' && 'min-h-32 min-w-32',
				size === 'medium' && 'min-h-56 min-w-56',
				size === 'large' && 'min-h-72 min-w-72',
				size === 'xLarge' && 'min-h-96 min-w-96',
				className,
			)}
		>
			{/* Whitespace after for screen readers and before to keep it centered */}
			{label && (
				<span
					className={cn(
						// Forced colors mode can add a background color to all texts,
						// ensure the label is placed above the main text to not have
						// it covered.
						'relative z-1',
						// Horizontal padding to keep too long texts inside the bubble.
						'px-2',
						size === 'small' && '-mb-0.5 text-xs',
						size === 'default' && '-mb-1 text-sm',
						size === 'medium' && '-mb-2 text-[1.125rem]',
						size === 'large' && '-mb-3 text-[1.5rem]',
						size === 'xLarge' && '-mb-4 text-[2rem]',
					)}
				>
					&nbsp;{label}&nbsp;
				</span>
			)}
			<span
				className={cn(
					'font-heading leading-none font-bold',
					// Horizontal padding to keep too long texts inside the bubble.
					'px-3',
					size === 'small' && 'text-[2.25rem]',
					size === 'default' && 'text-[3rem]',
					size === 'medium' && 'text-[5.5rem]',
					size === 'large' && 'text-[7.5rem]',
					size === 'xLarge' && 'text-[10rem]',
				)}
			>
				{text}
			</span>
		</div>
	);
}
DiscountBubble.displayName = 'DiscountBubble';
